import React, { useState, useEffect, useRef } from 'react';
import './ExportModal.scss';

const ExportModal = ({ isOpen, onClose, onExport, dashboardData }) => {
  const [exportType, setExportType] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [orientation, setOrientation] = useState('portrait');
  const [contentType, setContentType] = useState('both'); // New state for content type
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener when the modal is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose])


  const getTodayData = () => {
    const today = new Date().toISOString().split('T')[0];
    return dashboardData[today] || { tasks: [], hearings: [] };
  };

  const getRangeData = () => {
    const filtered = {};
    const start = new Date(startDate);
    const end = new Date(endDate);

    Object.keys(dashboardData).forEach(date => {
      const current = new Date(date);
      if (current >= start && current <= end) {
        filtered[date] = dashboardData[date];
      }
    });

    return filtered;
  };

  const filterDataByContentType = (data) => {
    const filteredData = {};

    Object.keys(data).forEach(date => {
      const dayData = data[date];
      const filteredDayData = {};

      if (contentType === 'tasks' || contentType === 'both') {
        filteredDayData.tasks = dayData.tasks || [];
      } else {
        filteredDayData.tasks = [];
      }

      if (contentType === 'hearings' || contentType === 'both') {
        filteredDayData.hearings = dayData.hearings || [];
      } else {
        filteredDayData.hearings = [];
      }

      filteredData[date] = filteredDayData;
    });

    return filteredData;
  };

  const handleExport = () => {
    let dataToExport;

    if (exportType === 'today') {
      const todayData = getTodayData();
      const todayFormatted = { [new Date().toISOString().split('T')[0]]: todayData };
      dataToExport = todayFormatted;
    } else if (exportType === 'range') {
      dataToExport = getRangeData();
    } else {
      dataToExport = dashboardData;
    }

    // Filter data based on content type selection
    const filteredData = filterDataByContentType(dataToExport);

    // Pass filtered data and orientation
    onExport(filteredData, orientation, contentType);
    onClose();
  };

  if (!isOpen) return null;

  const todayData = getTodayData();
  const hasDataToday = todayData.tasks.length > 0 || todayData.hearings.length > 0;

  const getContentDescription = () => {
    if (contentType === 'tasks') {
      return hasDataToday ? `Found ${todayData.tasks.length} tasks for today` : 'No tasks available for today';
    } else if (contentType === 'hearings') {
      return hasDataToday ? `Found ${todayData.hearings.length} hearings for today` : 'No hearings available for today';
    } else {
      return hasDataToday
        ? `Found ${todayData.tasks.length} tasks and ${todayData.hearings.length} hearings for today`
        : 'No data available for today - will export blank page';
    }
  };

  return (
    <div className="export-modal-backdrop">
      <div ref={modalRef} className="export-modal">
        <h2 className="export-modal-title">Export Dashboard Data</h2>

        <div className="export-modal-options">
          {/* Today Option */}
          <label className="export-modal-option">
            <input
              type="radio"
              value="today"
              checked={exportType === 'today'}
              onChange={(e) => setExportType(e.target.value)}
            />
            <span>Export Today's Data</span>
          </label>
          <p className="export-modal-desc">
            {getContentDescription()}
          </p>

          {/* Range Option */}
          <label className="export-modal-option">
            <input
              type="radio"
              value="range"
              checked={exportType === 'range'}
              onChange={(e) => setExportType(e.target.value)}
            />
            <span>Export Custom Date Range</span>
          </label>
          <div className="export-modal-range">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={exportType !== 'range'}
            />
            <span>to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={exportType !== 'range'}
            />
          </div>
          {exportType === 'range' && (
            <p className="export-modal-desc">
              Exporting data from <b>{startDate || '--'}</b> to <b>{endDate || '--'}</b>
            </p>
          )}
        </div>

        {/* Content Type Selection */}
        <div className="export-modal-content-type">
          <h3 className="content-type-title">Content to Export</h3>
          <div className="content-type-options">
            <label className="content-type-option">
              <input
                type="radio"
                value="both"
                checked={contentType === 'both'}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Tasks & Hearings</span>
            </label>
            <label className="content-type-option">
              <input
                type="radio"
                value="tasks"
                checked={contentType === 'tasks'}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Tasks Only</span>
            </label>
            <label className="content-type-option">
              <input
                type="radio"
                value="hearings"
                checked={contentType === 'hearings'}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Hearings Only</span>
            </label>
          </div>
        </div>

        {/* Orientation Selection */}
        <div className="export-modal-orientation">
          <h3 className="orientation-title">Page Orientation</h3>
          <div className="orientation-options">
            <label className="orientation-option">
              <input
                type="radio"
                value="portrait"
                checked={orientation === 'portrait'}
                onChange={(e) => setOrientation(e.target.value)}
              />
              <span>Portrait</span>
            </label>
            <label className="orientation-option">
              <input
                type="radio"
                value="landscape"
                checked={orientation === 'landscape'}
                onChange={(e) => setOrientation(e.target.value)}
              />
              <span>Landscape</span>
            </label>
          </div>
        </div>

        <div className="export-modal-actions">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="btn-export"
            onClick={handleExport}
            disabled={exportType === 'range' && (!startDate || !endDate)}
          >
            Export to PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;